import React, { useState } from "react";
import {
  Button,
  Typography,
  Box,
  CircularProgress,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../api/firebase";

const CountReiNoInoriDocs = () => {
  const [totalCount, setTotalCount] = useState(null);
  const [commentExistsCount, setCommentExistsCount] = useState(null);
  const [mailedByCount, setMailedByCount] = useState(null);

  const [processedCount, setProcessedCount] = useState(0);
  const [mailList, setMailList] = useState([]);
  const [mailedByList, setMailedByList] = useState([]);

  const [loadingTotal, setLoadingTotal] = useState(false);
  const [loadingComment, setLoadingComment] = useState(false);
  const [loadingMailedBy, setLoadingMailedBy] = useState(false);

  const [error, setError] = useState("");

  const handleCountTotal = async () => {
    setLoadingTotal(true);
    setError("");
    try {
      const collRef = collection(db, "ReiNoInori");
      const snapshot = await getCountFromServer(collRef);
      setTotalCount(snapshot.data().count);
    } catch (err) {
      console.error("総件数取得エラー:", err);
      setError("総件数の取得に失敗しました。");
    } finally {
      setLoadingTotal(false);
    }
  };

  const handleCountCommentExists = async () => {
    setLoadingComment(true);
    setError("");
    setProcessedCount(0);
    setCommentExistsCount(null);
    setMailList([]);

    try {
      const collRef = collection(db, "ReiNoInori");
      const pageSize = 1000;
      let lastDoc = null;
      let totalMatch = 0;
      let processed = 0;
      let resultMails = [];

      while (true) {
        const q = lastDoc
          ? query(collRef, orderBy("__name__"), startAfter(lastDoc), limit(pageSize))
          : query(collRef, orderBy("__name__"), limit(pageSize));

        const snapshot = await getDocs(q);
        if (snapshot.empty) break;

        snapshot.forEach((doc) => {
          const data = doc.data();
          if (
            data.hasOwnProperty("comment") &&
            data.comment !== null &&
            String(data.comment).trim() !== ""
          ) {
            totalMatch++;
            if (data.mail) {
              resultMails.push(data.mail);
            }
          }
        });

        processed += snapshot.docs.length;
        setProcessedCount(processed);

        lastDoc = snapshot.docs[snapshot.docs.length - 1];
        if (snapshot.docs.length < pageSize) break;

        await new Promise((res) => setTimeout(res, 100));
      }

      setCommentExistsCount(totalMatch);
      setMailList(resultMails);
    } catch (err) {
      console.error("commentあり件数取得エラー:", err);
      setError("commentあり件数の取得に失敗しました。");
    } finally {
      setLoadingComment(false);
    }
  };

  const handleCountMailedByExists = async () => {
    setLoadingMailedBy(true);
    setError("");
    setMailedByCount(null);
    setMailedByList([]);

    try {
      const collRef = collection(db, "ReiNoInori");
      const pageSize = 1000;
      let lastDoc = null;
      let totalMatch = 0;
      let resultMails = [];

      while (true) {
        const q = lastDoc
          ? query(collRef, orderBy("__name__"), startAfter(lastDoc), limit(pageSize))
          : query(collRef, orderBy("__name__"), limit(pageSize));

        const snapshot = await getDocs(q);
        if (snapshot.empty) break;

        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.hasOwnProperty("mailedBy")) {
            totalMatch++;
            if (data.mail) {
              resultMails.push(data.mail);
            }
          }
        });

        lastDoc = snapshot.docs[snapshot.docs.length - 1];
        if (snapshot.docs.length < pageSize) break;

        await new Promise((res) => setTimeout(res, 100));
      }

      setMailedByCount(totalMatch);
      setMailedByList(resultMails);
    } catch (err) {
      console.error("mailedByあり件数取得エラー:", err);
      setError("mailedByあり件数の取得に失敗しました。");
    } finally {
      setLoadingMailedBy(false);
    }
  };

  return (
    <Box textAlign="center" mt={4}>
      {/* 総件数取得 */}
      <Button variant="contained" onClick={handleCountTotal} disabled={loadingTotal}>
        ReiNoInori の総ドキュメント数を取得
      </Button>

      <Box mt={2}>
        {loadingTotal && <CircularProgress />}
        {!loadingTotal && totalCount !== null && (
          <Typography variant="h6">
            総ドキュメント数: {totalCount.toLocaleString()} 件
          </Typography>
        )}
      </Box>

      {/* commentありカウント */}
      <Box mt={4}>
        <Button
          variant="outlined"
          onClick={handleCountCommentExists}
          disabled={loadingComment}
        >
          comment が存在し、値がある件数を全件カウント＋メール表示
        </Button>

        <Box mt={2}>
          {loadingComment && (
            <>
              <CircularProgress />
              <Typography variant="body2" mt={2}>
                処理中... 現在 {processedCount.toLocaleString()} 件をスキャン中
              </Typography>
              <LinearProgress sx={{ mt: 2, width: "60%", mx: "auto" }} />
            </>
          )}
          {!loadingComment && commentExistsCount !== null && (
            <>
              <Typography variant="h6" mt={2}>
                commentあり: {commentExistsCount.toLocaleString()} 件（全件）
              </Typography>
              {mailList.length > 0 && (
                <Box mt={3} textAlign="left" mx="auto" maxWidth={400}>
                  <Typography variant="subtitle1" gutterBottom>
                    対象のメールアドレス一覧:
                  </Typography>
                  <List dense>
                    {mailList.map((mail, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={mail} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* mailedByありカウント */}
      <Box mt={4}>
        <Button
          variant="outlined"
          onClick={handleCountMailedByExists}
          disabled={loadingMailedBy}
        >
          mailedBy が存在する件数とメールを表示
        </Button>

        <Box mt={2}>
          {loadingMailedBy && (
            <>
              <CircularProgress />
              <Typography variant="body2" mt={2}>
                mailedBy を確認中…
              </Typography>
              <LinearProgress sx={{ mt: 2, width: "60%", mx: "auto" }} />
            </>
          )}
          {!loadingMailedBy && mailedByCount !== null && (
            <>
              <Typography variant="h6" mt={2}>
                mailedByあり: {mailedByCount.toLocaleString()} 件
              </Typography>
              {mailedByList.length > 0 && (
                <Box mt={3} textAlign="left" mx="auto" maxWidth={400}>
                  <Typography variant="subtitle1" gutterBottom>
                    対象のメールアドレス一覧:
                  </Typography>
                  <List dense>
                    {mailedByList.map((mail, idx) => (
                      <ListItem key={idx}>
                        <ListItemText primary={mail} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      {/* エラー */}
      {error && (
        <Typography color="error" mt={2}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CountReiNoInoriDocs;
